import { FC, memo, useMemo } from 'react';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Flex } from 'antd';
import {
  CellContext,
  ColumnDef,
  createColumnHelper,
} from '@tanstack/react-table';
import translations from './SystemClassifierItemTable.translations.json';
import {
  SystemClassifierItemDto,
  FormTableDefaultField,
  TanstackFormTable,
  FormTableSequenceNumberField,
  RequiredText,
  FormField,
  FormInput,
  getFormTableFieldName,
  TextEllipsis,
  CreateFormTableRowButton,
  DeleteFormTableRowButton,
  CloneFormTableRowButton,
} from '@atlas-frontend/sdk';

const { addTranslations, useTranslation } = i18nNsFactory(
  'system-classifier-item-table'
);
addTranslations(translations);

const columnHelper = createColumnHelper<SystemClassifierItemDto>();

export const SystemClassifierItemFormTable = ({
  isUserManaged,
}: {
  isUserManaged: boolean;
}) => {
  const { t } = useTranslation();

  const columns = useMemo(() => {
    const columns: ColumnDef<SystemClassifierItemDto, any>[] = [
      columnHelper.accessor('sequenceNumber', {
        header: t('column.sequenceNumber'),
        meta: { width: '14ch', className: 'sticky-left', textAlign: 'right' },
        sortingFn: 'basic',
        filterFn: 'includesString',
        cell: FormTableSequenceNumberField,
      }),
      columnHelper.accessor('code', {
        header: () => <RequiredText>{t('column.code')}</RequiredText>,
        meta: { minWidth: '25ch', className: 'sticky-left' },
        cell: (props) => <CodeCell {...props} isUserManaged={isUserManaged} />,
      }),
      columnHelper.accessor('name', {
        header: () => <RequiredText>{t('column.name')}</RequiredText>,
        cell: FormTableDefaultField,
      }),
      columnHelper.accessor('systemValue', {
        header: t('column.systemValue'),
        meta: { width: '17ch' },
        filterFn: 'includesOption',
        cell: (props) => (
          <TextEllipsis>{props.getValue() ? t('yes') : t('no')}</TextEllipsis>
        ),
      }),
      columnHelper.accessor('parameters', {
        header: t('column.parameters'),
        cell: FormTableDefaultField,
      }),
    ];

    if (isUserManaged) {
      columns.push(
        columnHelper.display({
          id: 'operations',
          header: () => (
            <Flex justify="center" gap="small">
              <CreateFormTableRowButton
                autoFocusField="code"
                defaultRowValues={getDefaultRowValues()}
              />
            </Flex>
          ),
          cell: (props) => {
            const { systemValue } = props.row.original;
            return (
              <Flex justify="center" gap="small">
                <CloneFormTableRowButton
                  context={props}
                  autoFocusField="clone"
                  resetId
                  resetValues={{ systemValue: false }}
                />
                {!systemValue && <DeleteFormTableRowButton context={props} />}
              </Flex>
            );
          },
          enableSorting: false,
          enableColumnFilter: false,
          meta: {
            width: 88,
            className: 'sticky-right',
          },
        })
      );
    }

    return columns;
  }, [isUserManaged, t]);

  const options = useMemo(
    () => ({
      systemValue: [
        { id: true, name: t('yes') },
        { id: false, name: t('no') },
      ],
    }),
    [t]
  );

  return (
    <Flex align="end" gap="middle" vertical>
      <TanstackFormTable
        name="systemClassifierItems"
        columns={columns}
        options={options}
        defaultSort={[{ id: 'sequenceNumber', desc: false }]}
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
      />
    </Flex>
  );
};

const CodeCell: FC<
  CellContext<SystemClassifierItemDto, any> & { isUserManaged: boolean }
> = memo((props) => {
  const name = getFormTableFieldName(props);
  const { isUserManaged } = props;
  const { systemValue } = props.row.original;
  const isEditable = isUserManaged && !systemValue;

  return (
    <FormField name={name} size="small" preview={{ previewOnly: !isEditable }}>
      <FormInput name={name} size="small" autoFocus />
    </FormField>
  );
});

const getDefaultRowValues = () => ({
  id: null,
  code: '',
  name: '',
  parameters: '',
  sequenceNumber: 0,
});
